import { Button } from '@change/ds-core-ui/Button';
import { Text } from '@change/ds-core-ui/Text';
import { ChevronLeftIcon } from '@change/ds-icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@change/ds-icons/ChevronRightIcon';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

// Button only styles native `disabled`, which would also drop focus from the arrow just pressed
const ARIA_DISABLED_CLASSES =
	'aria-disabled:text-typography-disabled aria-disabled:cursor-default aria-disabled:hover:bg-transparent! aria-disabled:active:bg-transparent!';

/**
 * Previous / next controls with a "1 of 3" counter. Each arrow is unavailable at its end but stays focusable, so
 * keyboard and screen-reader users keep their place; pressing it there does nothing, since the list can't scroll
 * further.
 */
export const DecisionMakerNavigation: React.FC<Props> = ({
	position,
	total,
	previousDisabled,
	nextDisabled,
	onPrevious,
	onNext,
}) => {
	const { translate } = useI18n();

	return (
		<div
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a fieldset groups form fields; prev/next controls take role="group", per the ARIA carousel pattern
			role="group"
			aria-label={translate('corgi.start_a_petition.decision_makers.navigation.label')}
			className="flex items-center justify-end gap-1"
		>
			<Button
				variant="tertiary"
				size="small"
				iconOnly
				leadingIcon={<ChevronLeftIcon />}
				aria-label={translate('corgi.start_a_petition.decision_makers.navigation.previous')}
				aria-disabled={previousDisabled || undefined}
				className={ARIA_DISABLED_CLASSES}
				onClick={onPrevious}
			/>
			<Text as="output" size="small" className="text-typography-strong min-w-10 text-center whitespace-nowrap">
				<Translate
					value="corgi.start_a_petition.decision_makers.navigation.counter"
					replacements={{ current: position, total }}
				/>
			</Text>
			<Button
				variant="tertiary"
				size="small"
				iconOnly
				leadingIcon={<ChevronRightIcon />}
				aria-label={translate('corgi.start_a_petition.decision_makers.navigation.next')}
				aria-disabled={nextDisabled || undefined}
				className={ARIA_DISABLED_CLASSES}
				onClick={onNext}
			/>
		</div>
	);
};

type Props = {
	position: number;
	total: number;
	previousDisabled: boolean;
	nextDisabled: boolean;
	onPrevious: () => void;
	onNext: () => void;
};
